import React, { FC, useEffect, useState } from 'react'
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { getPlatformFieldsAsync } from 'src/redux/actions/productActions'
import {
  getCategoryTemplateUsingTemplateIdAsync,
  getSelectedCategoryTreeAsync,
  postFieldDataAttributesAsync,
  updateCategoryTemplateDataAsync,
} from 'src/redux/actions/templateAndPricingActions'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import {
  platformAllowsEmptyCategoryAttributes,
  REQUIRED_CATEGORY_ATTRIBUTES_SAVE_MESSAGE,
  validateRequiredCategoryAttributesBeforeSave,
} from './utils/categoryAttributeIdentity'
import {
  CATEGORY_ITEM,
  CATEGORY_ITEM_ON_UPDATE,
} from 'src/interfaces/Template.interface'
import Loader from 'src/utils/Loader'
import { toast } from 'react-toastify'
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import { Formik } from 'formik'
import { useUserHavingWriteAccess } from 'src/utils/useUserHavingWriteAccess'
import { usePermission } from 'src/hooks/usePermission'
import CategoryMappingEngine, {
  CategoryMappingChangeData,
  MANUAL_MAP_PLATFORMS,
} from './CategoryMappingEngine'

type AddNewStoreTemplateModalPropsType = {
  isVisible: boolean
  closeModal: () => void
  selectedSecondaryPlatform: PlatformDataType | null
  getLatestCategoryTemplateList: () => void
  categoryData: CATEGORY_ITEM | null
}

const AddNewCategoryTemplateModal: FC<AddNewStoreTemplateModalPropsType> = ({
  isVisible,
  closeModal,
  selectedSecondaryPlatform,
  categoryData,
  getLatestCategoryTemplateList,
}) => {
  const { isRead, isWrite, isDelete } = usePermission()
  const { isUserHavingWriteAccess } = useUserHavingWriteAccess()
  const dispatch = useAppDispatch()
  const categoryAttributeDefinitions = useAppSelector(
    (state) => state.templateAndPricing.getAttributeData
  )

  // ── engine integration state ──
  const [engineState, setEngineState] = useState<CategoryMappingChangeData>({
    categoryId: null,
    categoryPath: '',
    isLeafCategory: false,
    showAttributes: false,
    isAttributeLoading: false,
  })
  const [initialCategoryId, setInitialCategoryId] = useState<number | null>(null)
  const [initialCategoryPath, setInitialCategoryPath] = useState<string>('')
  const [initialPlatformFieldId, setInitialPlatformFieldId] = useState<number | null>(null)

  // ── modal-owned state ──
  const [finalPayload, setFinalPayload] = useState<any>()
  const [categoryName, setCategoryName] = useState('')
  const [attributesInUpdateCase, setAttributesInUpdateCase] =
    useState<CATEGORY_ITEM_ON_UPDATE | null>(null)
  const [categoryLoader, setCategoryLoader] = useState(true)
  const isEditMode = Boolean(categoryData?.id)

  const normalizeAttributes = (attributes: any[] = []) =>
    attributes
      .map((attribute) => ({
        attribute_field:
          typeof attribute.attribute_field === 'object'
            ? attribute.attribute_field?.id ?? null
            : attribute.attribute_field ?? null,
        value:
          typeof attribute.value === 'object'
            ? attribute.value?.id ?? attribute.value?.value ?? null
            : attribute.value ?? null,
        value_str: attribute.value_str ?? null,
        mapped_field:
          typeof attribute.mapped_field === 'object'
            ? attribute.mapped_field?.id ?? null
            : attribute.mapped_field ?? null,
        unit:
          typeof attribute.unit === 'object'
            ? attribute.unit?.id ?? attribute.unit?.value ?? null
            : attribute.unit ?? null,
      }))
      .sort((first, second) => Number(first.attribute_field) - Number(second.attribute_field))

  const selectedPlatformCode = (
    selectedSecondaryPlatform?.code ||
    selectedSecondaryPlatform?.name ||
    selectedSecondaryPlatform?.platform_info?.name ||
    ''
  ).toUpperCase()

  const allowsEmptyAttributes = platformAllowsEmptyCategoryAttributes(
    selectedPlatformCode,
    MANUAL_MAP_PLATFORMS
  )

  const handleAttributeSelection = (selectedAttributes) => {
    if (!selectedAttributes) {
      console.error('Attributes data not available')
      return
    }
    const payload = {
      platform_id: selectedSecondaryPlatform?.id,
      name: categoryName,
      category_data: engineState.categoryId,
      attributes: selectedAttributes,
    }
    setFinalPayload(payload)
  }

  useEffect(() => {
    if (!isVisible || !selectedSecondaryPlatform?.id) return
    setCategoryLoader(true)
    if (categoryData) {
      Promise.all([
        dispatch(
          getSelectedCategoryTreeAsync({ category_data_id: categoryData.category_data.id })
        ).then((response) => {
          if (response.payload?.length) {
            const path = response.payload
            setInitialCategoryId(path[path.length - 1].id)
            setInitialCategoryPath(path.map((it) => it.api_name).join(' > '))
          }
        }),

        dispatch(
          getCategoryTemplateUsingTemplateIdAsync({ category_temp_id: categoryData.id })
        ).then((response) => {
          setAttributesInUpdateCase(response.payload)
          setInitialCategoryId((prev) => prev ?? categoryData.category_data.id)
          setInitialCategoryPath(
            (prev) => prev || categoryData.category_data.api_name || ''
          )
          setInitialPlatformFieldId(
            categoryData?.category_data?.platform_field ||
            categoryData?.platform_field?.id ||
            null
          )
        }),
      ]).finally(() => {
        setCategoryLoader(false)
      })
    } else {
      dispatch(
        getPlatformFieldsAsync({
          platformId: selectedSecondaryPlatform.id,
          otherParams: { field_code: 'CATEGORY' },
        })
      ).finally(() => {
        setCategoryLoader(false)
      })
    }
  }, [isVisible, selectedSecondaryPlatform?.id])

  const renderTooltip = (props) => (
    <Tooltip id='button-tooltip' {...props}>
      Use this Switch when you want all Products to use this Mapping/Rule.
    </Tooltip>
  )

  type INITIAL_VALUES = {
    name: string
    isDefault: boolean
  }

  const initialValues: INITIAL_VALUES = {
    name: categoryData?.name ? categoryData?.name : '',
    isDefault: categoryData?.isDefault ? categoryData.isDefault : false,
  }

  return (
    <>
      <Modal
        size={'xl'}
        backdrop='static'
        className='pl-0'
        centered
        show={isVisible}
        onHide={closeModal}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={(values, helpers) => {
            helpers.setSubmitting(true)
            let cop = { ...finalPayload }
            cop.name = values.name
            cop.isDefault = values.isDefault
            cop.platform_id = selectedSecondaryPlatform?.id
            if (!engineState.categoryId) {
              toast.error('Please select a category')
              helpers.setSubmitting(false)
              return
            }
            cop.category_data = engineState.categoryId

            const attributes = cop.attributes || []
            if (!attributes.length && !allowsEmptyAttributes) {
              toast.error('No Attributes and Values to save')
              helpers.setSubmitting(false)
              return
            }
            const categoryAttributeValidation = validateRequiredCategoryAttributesBeforeSave({
              categoryId: engineState.categoryId,
              isAttributeLoading: engineState.isAttributeLoading,
              categoryAttributes: categoryAttributeDefinitions,
              selectedAttributes: attributes,
            })
            if (!categoryAttributeValidation.ok) {
              toast.error(REQUIRED_CATEGORY_ATTRIBUTES_SAVE_MESSAGE)
              helpers.setSubmitting(false)
              return
            }

            if (categoryData) {
              dispatch(
                updateCategoryTemplateDataAsync({
                  category_template: categoryData.id,
                  payloadupdate: { ...cop },
                })
              ).then((response) => {
                if (response.payload?.data?.error) {
                  toast.error(response.payload?.data?.error)
                  helpers.setSubmitting(false)
                }

                if (response.payload?.id) {
                  toast.success('Category mapping updated successfully')
                  helpers.setSubmitting(false)
                  getLatestCategoryTemplateList()
                  closeModal()
                }
              })
            } else {
              dispatch(postFieldDataAttributesAsync(cop)).then((response) => {
                if (response.payload?.data?.error) {
                  toast.error(response.payload?.data?.error)
                  helpers.setSubmitting(false)
                }

                if (response.payload?.id) {
                  toast.success('Category mapping created successfully')
                  helpers.setSubmitting(false)
                  getLatestCategoryTemplateList()
                  closeModal()
                }
              })
            }
          }}
        >
          {({ values, handleSubmit, handleChange, isSubmitting, dirty }) => {
            const existingCategoryId = categoryData?.category_data?.id ?? null
            const hasCategoryChanged =
              engineState.categoryId !== null &&
              existingCategoryId !== null &&
              engineState.categoryId !== existingCategoryId
            const hasAttributeChanges =
              isEditMode &&
              JSON.stringify(normalizeAttributes(finalPayload?.attributes || [])) !==
              JSON.stringify(
                normalizeAttributes(attributesInUpdateCase?.attributes || [])
              )

            const canSubmit =
              !isSubmitting &&
              !engineState.isAttributeLoading &&
              engineState.categoryId !== null &&
              (isEditMode
                ? dirty || hasCategoryChanged || hasAttributeChanges
                : engineState.isLeafCategory)

            return (
              <>
                <form action='' onSubmit={handleSubmit}>
                  <Modal.Header>
                    <Modal.Title>
                      {categoryData?.id
                        ? 'Update Category Mapping'
                        : 'Add New Category Mapping'}
                    </Modal.Title>
                    <div className='d-inline-flex'>
                      <Button
                        size='sm'
                        type='button'
                        className='mx-2'
                        onClick={() => {
                          closeModal()
                          setAttributesInUpdateCase(null)
                          setInitialCategoryId(null)
                          setInitialCategoryPath('')
                          setInitialPlatformFieldId(null)
                        }}
                        disabled={isSubmitting}
                      >
                        Close
                      </Button>
                      {((!categoryData?.id && isWrite) ||
                        (categoryData?.id && isWrite)) &&
                        isUserHavingWriteAccess() && (
                          <Button
                            size='sm'
                            type='submit'
                            variant='success'
                            disabled={!canSubmit || !isUserHavingWriteAccess()}
                          >
                            {isSubmitting ? `Saving` : `Save Changes`}
                          </Button>
                        )}
                    </div>
                  </Modal.Header>
                  <Modal.Body>
                    {categoryLoader || isSubmitting ? (
                      <Loader />
                    ) : (
                      <div>
                        {false && (
                          <div>
                            <div className='d-flex justify-content-end form-check form-switch mt-2 ps-2'>
                              <div>
                                <label className='form-label mt-1'>
                                  Default&nbsp;
                                  <OverlayTrigger
                                    placement='left'
                                    overlay={renderTooltip}
                                  >
                                    <i className='bi bi-info-circle'></i>
                                  </OverlayTrigger>
                                </label>
                              </div>
                              <input
                                className='ms-4 mb-4 form-check-input h-20px w-40px'
                                type='checkbox'
                                checked={values.isDefault}
                                name='isDefault'
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        )}
                        <div className='mb-8'>
                          <span className='required-sm'>
                            <label className='form-label'>Rule name</label>
                          </span>
                          <input
                            name='name'
                            className='form-control'
                            placeholder='Rule name'
                            value={values.name}
                            onChange={handleChange}
                          />
                        </div>

                        <CategoryMappingEngine
                          selectedSecondaryPlatform={selectedSecondaryPlatform}
                          isEditMode={isEditMode}
                          initialCategoryId={initialCategoryId}
                          initialCategoryPath={initialCategoryPath}
                          initialPlatformFieldId={initialPlatformFieldId}
                          updatingMapData={attributesInUpdateCase}
                          inputAttributesKey={
                            categoryData ? String(categoryData.id) : ''
                          }
                          onCategoryChange={setEngineState}
                          onAttributeSelection={handleAttributeSelection}
                        />
                      </div>
                    )}
                  </Modal.Body>
                </form>
              </>
            )
          }}
        </Formik>
      </Modal>
    </>
  )
}

export default AddNewCategoryTemplateModal
